import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  fetchSuperadminPaymentsStats,
  type PaymentStatsDto
} from '../../features/superadmin/api/payments-stats.api';
import type { ApiClient } from '../../shared/api/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../shared/ui/Card';
import { FieldHelpIcon } from '../../shared/ui/FieldHelpIcon';
import { Spinner } from '../../shared/ui/Spinner';

function formatRub(value: number): string {
  return `${value.toLocaleString('ru-RU')} ₽`;
}

function toLocalDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromInputToISO(s: string): string {
  if (!s) return '';
  return new Date(s + 'T00:00:00').toISOString();
}

export default function PaymentStatsBlock({ apiClient }: { apiClient: ApiClient }) {
  const today = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() - 30);
    return toLocalDateInput(d);
  }, [today]);
  const defaultTo = toLocalDateInput(today);

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaymentStatsDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchSuperadminPaymentsStats(apiClient, fromInputToISO(f), fromInputToISO(t));
      setData(d);
    } catch {
      setError('Не удалось загрузить статистику платежей');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    void load(from, to);
  }, [from, to, load]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle>Платежи</CardTitle>
            <CardDescription>Статистика успешных платежей по всем организациям.</CardDescription>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <label className="text-xs text-[hsl(var(--ui-muted-foreground))]">с</label>
            <input
              type="date"
              className="h-8 rounded border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-2 text-xs"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <label className="text-xs text-[hsl(var(--ui-muted-foreground))]">по</label>
            <input
              type="date"
              className="h-8 rounded border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] px-2 text-xs"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Spinner label="Загрузка статистики платежей…" />
        ) : error ? (
          <p className="text-sm text-[hsl(var(--ui-destructive))]">{error}</p>
        ) : data ? (
          <div className="space-y-4">
            {/* Ключевые метрики */}
            <div className="grid gap-3 sm:grid-cols-3">
              <MetricCard
                label="Выручка за период"
                value={formatRub(data.total_revenue)}
                hint="Сумма успешных платежей в указанном диапазоне."
              />
              <MetricCard
                label="Платежей"
                value={data.total_count.toLocaleString('ru-RU')}
                hint="Количество успешных транзакций."
              />
              <div className="rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">Текущий месяц</p>
                  <FieldHelpIcon text="Выручка с начала месяца и изменение к предыдущему." />
                </div>
                <p className="mt-2 text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]">
                  {formatRub(data.month_current_total)}
                </p>
                {data.month_delta_pct != null && (
                  <p className={`text-xs ${data.month_delta_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {data.month_delta_pct >= 0 ? '+' : ''}{data.month_delta_pct.toFixed(1)}% к прошлому месяцу
                  </p>
                )}
              </div>
            </div>

            {/* По провайдерам */}
            {data.by_provider.length > 0 && (
              <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">По провайдерам</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[hsl(var(--ui-muted-foreground))]">
                      <th className="pb-1 font-medium">Провайдер</th>
                      <th className="pb-1 font-medium text-right">Платежей</th>
                      <th className="pb-1 font-medium text-right">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_provider.map((p) => (
                      <tr key={p.provider} className="border-t border-[hsl(var(--ui-border))]">
                        <td className="py-1 capitalize">{p.provider}</td>
                        <td className="py-1 text-right tabular-nums">{p.count.toLocaleString('ru-RU')}</td>
                        <td className="py-1 text-right tabular-nums font-medium">{formatRub(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* По назначению */}
            {data.by_reference_type != null && data.by_reference_type.length > 0 && (
              <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">По назначению</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[hsl(var(--ui-muted-foreground))]">
                      <th className="pb-1 font-medium">Тип</th>
                      <th className="pb-1 font-medium text-right">Платежей</th>
                      <th className="pb-1 font-medium text-right">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_reference_type.map((r) => (
                      <tr key={r.reference_type} className="border-t border-[hsl(var(--ui-border))]">
                        <td className="py-1">{r.reference_type || '(без типа)'}</td>
                        <td className="py-1 text-right tabular-nums">{r.count.toLocaleString('ru-RU')}</td>
                        <td className="py-1 text-right tabular-nums font-medium">{formatRub(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* По организациям */}
            {data.per_org.length > 0 && (
              <div className="rounded-sm border border-[hsl(var(--ui-border))] p-3">
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">Топ организаций</p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[hsl(var(--ui-muted-foreground))]">
                      <th className="pb-1 font-medium">Организация</th>
                      <th className="pb-1 font-medium text-right">Платежей</th>
                      <th className="pb-1 font-medium text-right">Сумма</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.per_org.map((o) => (
                      <tr key={o.org_id} className="border-t border-[hsl(var(--ui-border))]">
                        <td className="py-1">{o.org_name || `ID ${o.org_id}`}</td>
                        <td className="py-1 text-right tabular-nums">{o.count.toLocaleString('ru-RU')}</td>
                        <td className="py-1 text-right tabular-nums font-medium">{formatRub(o.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Недельный график */}
            {data.weekly_series.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">
                  Выручка по неделям
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={data.weekly_series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--ui-border))" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
                    <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--ui-muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        background: 'hsl(var(--ui-card))',
                        border: '1px solid hsl(var(--ui-border))',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}
                      formatter={(value: unknown) => `${(value as number ?? 0).toLocaleString('ru-RU')} ₽`}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      name="Выручка"
                      stroke="hsl(262, 83%, 58%)"
                      fill="hsl(262, 83%, 58%)"
                      fillOpacity={0.2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-sm border border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-card))] p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium text-[hsl(var(--ui-muted-foreground))]">{label}</p>
        {hint ? <FieldHelpIcon text={hint} /> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-[hsl(var(--ui-foreground))]">{value}</p>
    </div>
  );
}
